import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonService } from '../../common-service';
import { environment } from '../../../../../environments/environment';
import { ConfiguracaoNFCeRequest } from '../../requests/dfe/configuracao-nfce.request';
import { ExcecaoRejeicaoNFCeRequest } from '../../requests/dfe/excecao-rejeicao-nfce.request';
import { SyncExcecoesRejeicaoNFCeRequest } from '../../requests/dfe/sync-excecoes-rejeicao-nfce.request';
import { ConfiguracaoNFCeResponse } from '../../responses/dfe/configuracao-nfce.response';
import { ExcecaoRejeicaoNFCeResponse } from '../../responses/dfe/excecao-rejeicao-nfce.response';
import { ListaExcecaoRejeicaoNFCeResponse } from '../../responses/dfe/lista-excecao-rejeicao-nfce.response';

@Injectable({
  providedIn: 'root',
})
export class ConfiguracaoNFCeService extends CommonService {
  constructor(
    private httpClient: HttpClient
  ) {
    super();
  }

  private readonly apiUrl = `${environment.urlApiDFe}ConfiguracaoNFCe`;

  // ---- Configuração NFCe ----

  /**
   * Busca a configuração NFCe da empresa logada.
   * [GET] /ConfiguracaoNFCe
   */
  public get(): Observable<ConfiguracaoNFCeResponse> {
    return this.httpClient
      .get<ConfiguracaoNFCeResponse>(this.apiUrl, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Cria ou atualiza a configuração NFCe da empresa logada.
   * [POST] /ConfiguracaoNFCe
   */
  public save(request: ConfiguracaoNFCeRequest): Observable<ConfiguracaoNFCeResponse> {
    return this.httpClient
      .post<ConfiguracaoNFCeResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // ---- Exceções de Rejeição NFCe ----

  /**
   * Busca todas as exceções de rejeição NFCe da empresa.
   * [GET] /ConfiguracaoNFCe/excecao-rejeicao
   */
  public getExcecoesRejeicao(): Observable<ListaExcecaoRejeicaoNFCeResponse> {
    const url = `${this.apiUrl}/excecao-rejeicao`;
    return this.httpClient
      .get<ListaExcecaoRejeicaoNFCeResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Sincroniza a lista completa de exceções de rejeição.
   * O backend faz diff com o banco: insere novas, atualiza alteradas, remove ausentes.
   * [PUT] /ConfiguracaoNFCe/excecao-rejeicao/sync
   */
  public syncExcecoes(request: SyncExcecoesRejeicaoNFCeRequest): Observable<ListaExcecaoRejeicaoNFCeResponse> {
    const url = `${this.apiUrl}/excecao-rejeicao/sync`;
    return this.httpClient
      .put<ListaExcecaoRejeicaoNFCeResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Cria uma nova exceção de rejeição.
   * [POST] /ConfiguracaoNFCe/excecao-rejeicao
   */
  public createExcecao(request: ExcecaoRejeicaoNFCeRequest): Observable<ExcecaoRejeicaoNFCeResponse> {
    const url = `${this.apiUrl}/excecao-rejeicao`;
    return this.httpClient
      .post<ExcecaoRejeicaoNFCeResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Atualiza uma exceção de rejeição existente.
   * [PUT] /ConfiguracaoNFCe/excecao-rejeicao/{id}
   */
  public updateExcecao(
    id: number,
    request: ExcecaoRejeicaoNFCeRequest
  ): Observable<ExcecaoRejeicaoNFCeResponse> {
    const url = `${this.apiUrl}/excecao-rejeicao/${id}`;
    return this.httpClient
      .put<ExcecaoRejeicaoNFCeResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Remove uma exceção de rejeição.
   * [DELETE] /ConfiguracaoNFCe/excecao-rejeicao/{id}
   */
  public deleteExcecao(id: number): Observable<ExcecaoRejeicaoNFCeResponse> {
    const url = `${this.apiUrl}/excecao-rejeicao/${id}`;
    return this.httpClient
      .delete<ExcecaoRejeicaoNFCeResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
